import type { StudioScreenshotMode } from './previewInspect.js';
import {
  isPreviewMessageOrigin,
  isStudioScreenshotResult,
  previewPostMessageOrigin,
  studioScreenshotRequest,
} from './previewInspect.js';

/** Always available: capture runs inside the preview iframe via postMessage. */
export function isPreviewScreenshotSupported(): boolean {
  return true;
}

export function previewScreenshotFileName(nowMs: number = Date.now()): string {
  return `aperçu-${String(nowMs)}.png`;
}

export function selectionScreenshotFileName(nowMs: number = Date.now()): string {
  return `sélection-${String(nowMs)}.png`;
}

export type CapturePreviewScreenshotInput = {
  readonly iframe: HTMLIFrameElement;
  readonly previewUrl: string;
  readonly mode: StudioScreenshotMode;
  readonly selector?: string;
  readonly fileName?: string;
  readonly timeoutMs?: number;
};

type ScreenshotFinishOk = {
  readonly kind: 'ok';
  readonly dataUrl: string;
};

type ScreenshotFinishError = {
  readonly kind: 'error';
  readonly reason: Error;
};

type ScreenshotFinishOutcome = ScreenshotFinishOk | ScreenshotFinishError;

/**
 * Ask the preview iframe to rasterize itself (or a selected element) via html2canvas.
 * Avoids getDisplayMedia so the user never shares the whole screen.
 */
export async function capturePreviewScreenshot(
  input: CapturePreviewScreenshotInput
): Promise<File> {
  const frame = input.iframe.contentWindow;
  const targetOrigin = previewPostMessageOrigin(input.previewUrl);
  if (frame === null || targetOrigin === undefined) {
    throw new Error('screenshot-no-frame');
  }
  const requestId = crypto.randomUUID();
  const timeoutMs = input.timeoutMs ?? 15_000;
  const dataUrl = await new Promise<string>((resolve, reject) => {
    let settled = false;
    function finish(outcome: ScreenshotFinishOutcome): void {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      if (outcome.kind === 'ok') {
        resolve(outcome.dataUrl);
        return;
      }
      reject(outcome.reason);
    }
    function onMessage(event: MessageEvent): void {
      if (!isPreviewMessageOrigin(event.origin, input.previewUrl)) {
        return;
      }
      if (!isStudioScreenshotResult(event.data)) {
        return;
      }
      if (event.data.requestId !== requestId) {
        return;
      }
      if (event.data.ok !== true || typeof event.data.dataUrl !== 'string') {
        finish({ kind: 'error', reason: new Error('screenshot-failed') });
        return;
      }
      finish({ kind: 'ok', dataUrl: event.data.dataUrl });
    }
    window.addEventListener('message', onMessage);
    frame.postMessage(studioScreenshotRequest(requestId, input.mode, input.selector), targetOrigin);
    const timer = window.setTimeout(() => {
      finish({ kind: 'error', reason: new Error('screenshot-timeout') });
    }, timeoutMs);
  });
  const blob = await dataUrlToPngBlob(dataUrl);
  return new File(
    [blob],
    input.fileName ??
      (input.mode === 'selection' ? selectionScreenshotFileName() : previewScreenshotFileName()),
    { type: 'image/png' }
  );
}

async function dataUrlToPngBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  if (blob.size < 1) {
    throw new Error('screenshot-empty');
  }
  return blob;
}
